import get from 'lodash/get';
import HttpRequest from '@Services/RequestFactory';
import Auth from '@Services/Auth';
import { HEATMAP_VIEW_PARAMS } from '@Pages/Heatmap/constants';

class HeatmapDataService {
  _cachedData = null;

  _checkNextParams = (heatmap, nextParams) => {
    Object.assign(nextParams, {
      firstRow: Math.max(nextParams.firstRow, 0),
      firstColumn: Math.max(nextParams.firstColumn, 0),
      lastRow: Math.min(nextParams.lastRow, heatmap.viewOptions.rowsAmount),
      lastColumn: Math.min(nextParams.lastColumn, heatmap.viewOptions.colsAmount),
    });
  };

  _checkDataLimits = (heatmap, requestParams) => {
    let firstRow = Math.max(requestParams.firstRow, 0);
    let firstColumn = Math.max(requestParams.firstColumn, 0);
    let lastRow = Math.min(requestParams.lastRow, heatmap.viewOptions.rowsAmount - 1);
    let lastColumn = Math.min(requestParams.lastColumn, heatmap.viewOptions.colsAmount - 1);

    // Correction on maxRequestColsRows
    const totalRequestRows = lastRow - firstRow + 1;
    if (totalRequestRows > HEATMAP_VIEW_PARAMS.maxRequestColsRows) {
      const rowsToCut = totalRequestRows - HEATMAP_VIEW_PARAMS.maxRequestColsRows;

      if (firstRow === 0) {
        // At top edge → trim only from the bottom
        lastRow -= rowsToCut;
      } else if (lastRow === heatmap.viewOptions.rowsAmount - 1) {
        // At bottom edge → trim only from the top
        firstRow += rowsToCut;
      } else {
        // In the middle → trim evenly
        firstRow += Math.ceil(rowsToCut / 2);
        lastRow -= Math.floor(rowsToCut / 2);
      }
    }

    const totalRequestCols = lastColumn - firstColumn + 1;
    if (totalRequestCols > HEATMAP_VIEW_PARAMS.maxRequestColsRows) {
      const colsToCut = totalRequestCols - HEATMAP_VIEW_PARAMS.maxRequestColsRows;

      if (firstColumn === 0) {
        // At left edge → trim only from the right
        lastColumn -= colsToCut;
      } else if (lastColumn === heatmap.viewOptions.colsAmount - 1) {
        // At right edge → trim only from the left
        firstColumn += colsToCut;
      } else {
        // In the middle → trim evenly
        firstColumn += Math.ceil(colsToCut / 2);
        lastColumn -= Math.floor(colsToCut / 2);
      }
    }

    Object.assign(requestParams, {
      firstRow,
      firstColumn,
      lastRow,
      lastColumn,
    });
  };

  _prepareFetchDataRequest = (heatmap, requestParams) => {
    this._checkDataLimits(heatmap, requestParams);

    return {
      matrixName: heatmap.matrixName,
      dataDensity: true,
      rows: {
        first: requestParams.firstRow + 1,
        last: requestParams.lastRow + 1,
        headers: !!requestParams.rowHeaders,
      },
      cols: {
        first: requestParams.firstColumn + 1,
        last: requestParams.lastColumn + 1,
        headers: !!requestParams.columnHeaders,
      },
    };
  };

  _getNextRequestParams = (heatmap, newRowStart, newCellStart, rowsToShow, colsToShow) => {
    const rowsToShowWithBuffer = rowsToShow * heatmap.viewOptions.dataBufferSize;
    const colsToShowWithBuffer = colsToShow * heatmap.viewOptions.dataBufferSize;

    const requestRowFirst = Math.ceil(newRowStart + rowsToShow / 2 - rowsToShowWithBuffer / 2);
    const requestColumnFirst = Math.ceil(newCellStart + colsToShow / 2 - colsToShowWithBuffer / 2);
    const requestRowLast = Math.ceil(newRowStart + rowsToShow / 2 + rowsToShowWithBuffer / 2);
    const requestColumnLast = Math.ceil(newCellStart + colsToShow / 2 + colsToShowWithBuffer / 2);

    const nextParams = {
      firstRow: requestRowFirst,
      lastRow: requestRowLast,
      firstColumn: requestColumnFirst,
      lastColumn: requestColumnLast,
    };

    this._checkNextParams(heatmap, nextParams);

    return nextParams;
  };

  _getOverlaps(nextCachedData) {
    const statesRowsDiff = Math.max(
      Math.abs(this._cachedData.firstRow - nextCachedData.firstRow),
      Math.abs(this._cachedData.lastRow - nextCachedData.lastRow)
    );
    const currentTotalRows = this._cachedData.lastRow - this._cachedData.firstRow;
    const rowsOverlap = statesRowsDiff / currentTotalRows;

    const statesColsDiff = Math.max(
      Math.abs(this._cachedData.firstColumn - nextCachedData.firstColumn),
      Math.abs(this._cachedData.lastColumn - nextCachedData.lastColumn)
    );
    const currentTotalCols = this._cachedData.lastColumn - this._cachedData.firstColumn;
    const colsOverlap = statesColsDiff / currentTotalCols;

    return {
      rowsOverlap,
      colsOverlap,
      min: Math.min(rowsOverlap, colsOverlap),
      max: Math.max(rowsOverlap, colsOverlap),
    };
  }

  _cleanUpObject = (objectToClean) => {
    Object.keys(objectToClean).forEach((key) => {
      // eslint-disable-next-line no-param-reassign
      delete objectToClean[key];
    });
  };

  // fetch existing data to fill scrolled area
  _retrieveData(heatmap, nextCachedData, sendRequest) {
    const requestParams = {};
    let payload;
    const isRefreshData = true;

    // scrolled vertically
    if (nextCachedData.lastRow !== this._cachedData.lastRow) {
      const isScrollDown = nextCachedData.lastRow > this._cachedData.lastRow;

      Object.assign(requestParams, {
        firstRow: isScrollDown ? this._cachedData.lastRow : nextCachedData.firstRow,
        lastRow: isScrollDown ? nextCachedData.lastRow : this._cachedData.firstRow,
        firstColumn: this._cachedData.firstColumn,
        lastColumn: this._cachedData.lastColumn,
        rowHeaders: true,
      });

      payload = this._prepareFetchDataRequest(heatmap, requestParams);
      sendRequest(payload, isRefreshData);
    }

    // scrolled horizontally
    if (nextCachedData.lastColumn !== this._cachedData.lastColumn) {
      const isScrollRight = nextCachedData.lastColumn > this._cachedData.lastColumn;

      Object.assign(requestParams, {
        firstRow: this._cachedData.firstRow,
        lastRow: this._cachedData.lastRow,
        firstColumn: isScrollRight ? this._cachedData.lastColumn : nextCachedData.firstColumn,
        lastColumn: isScrollRight ? nextCachedData.lastColumn : this._cachedData.firstColumn,
        columnHeaders: true,
      });

      if (requestParams.firstColumn > requestParams.lastColumn) {
        // swap first and last column values in this case
        [requestParams.firstColumn, requestParams.lastColumn] = [requestParams.lastColumn, requestParams.firstColumn];
      }

      payload = this._prepareFetchDataRequest(heatmap, requestParams);
      sendRequest(payload, isRefreshData);
    }

    this._cachedData = nextCachedData;
  }

  // clear all data due to big amount of last scrolling value and load completely new set of values
  _refreshData(heatmap, nextParams, sendRequest) {
    const requestParams = { ...nextParams };

    // scrolled vertically
    if (nextParams.lastRow !== this._cachedData.lastRow) {
      this._cleanUpObject(heatmap.headers.rows);
      requestParams.rowHeaders = true;
    }
    // scrolled horizontally
    if (nextParams.lastColumn !== this._cachedData.lastColumn) {
      this._cleanUpObject(heatmap.headers.cols);
      requestParams.columnHeaders = true;
    }

    this._cleanUpObject(heatmap.data);
    const payload = this._prepareFetchDataRequest(heatmap, requestParams);
    const isRefreshData = true;
    sendRequest(payload, isRefreshData);
    this._cachedData = nextParams;
  }

  _getImagePath = (path) => {
    const imgUrl = get(path, 'url');

    if (!imgUrl) {
      return null;
    }

    const separator = imgUrl.indexOf('?') !== -1 ? '&' : '?';
    return HttpRequest.getUrlWithPrefix([imgUrl, separator, 'access_token=', Auth.getAccessToken()].join(''));
  };

  // Heatmap table headers, not networking headers...
  _extendHeaderData = (headersDataObj, payloadHeadersArr) => {
    payloadHeadersArr.forEach((header) => {
      if (headersDataObj[header.index - 1]) {
        Object.assign(headersDataObj[header.index - 1], header);
      } else {
        // eslint-disable-next-line no-param-reassign
        headersDataObj[header.index - 1] = header;
      }
    });
  };

  _extendMatrixData = (matrixDataObj, payloadMatrixArr, startRow, startColumn) => {
    payloadMatrixArr.forEach((currentRow, row) => {
      if (!matrixDataObj[startRow + row]) {
        // eslint-disable-next-line no-param-reassign
        matrixDataObj[startRow + row] = {};
      }

      currentRow.forEach((currentColumn, column) => {
        // eslint-disable-next-line no-param-reassign
        matrixDataObj[startRow + row][startColumn + column] = currentColumn;
      });
    });
  };

  _cleanUp(storedHeatmap) {
    Object.keys(storedHeatmap.data).forEach((row) => {
      const rowInt = parseInt(row, 10);

      if (rowInt < this._cachedData.firstRow || rowInt > this._cachedData.lastRow) {
        // eslint-disable-next-line no-param-reassign
        delete storedHeatmap.data[row];
      } else {
        Object.keys(storedHeatmap.data[row]).forEach((column) => {
          const columnInt = parseInt(column, 10);

          if (columnInt < this._cachedData.firstColumn || columnInt > this._cachedData.lastColumn) {
            // eslint-disable-next-line no-param-reassign
            delete storedHeatmap.data[row][column];
          }
        });
      }
    });
  }

  reset() {
    this._cachedData = {
      firstRow: 0,
      lastRow: 0,
      firstColumn: 0,
      lastColumn: 0,
    };
  }

  onInitData(heatmap, rowsToShow, colsToShow, sendRequest) {
    const rowsToShowWithBuffer = rowsToShow * heatmap.viewOptions.dataBufferSize;
    const colsToShowWithBuffer = colsToShow * heatmap.viewOptions.dataBufferSize;

    Object.assign(this._cachedData, {
      firstRow: 0,
      lastRow: Math.ceil(this._cachedData.firstRow + rowsToShow / 2 + rowsToShowWithBuffer / 2),
      firstColumn: 0,
      lastColumn: Math.ceil(this._cachedData.firstColumn + colsToShow / 2 + colsToShowWithBuffer / 2),
      rowHeaders: true,
      columnHeaders: true,
    });

    this._checkNextParams(heatmap, this._cachedData);

    const payload = this._prepareFetchDataRequest(heatmap, { ...this._cachedData });
    const isRefreshData = true;
    sendRequest(payload, isRefreshData);
  }

  onRequestData(heatmap, newRowStart, newCellStart, rowsToShow, colsToShow, sendRequest) {
    const nextCachedData = this._getNextRequestParams(heatmap, newRowStart, newCellStart, rowsToShow, colsToShow);

    // check for overlapping
    // if it is more than overlapToFetch then heatmap will request next portion of data
    const overlaps = this._getOverlaps(nextCachedData);

    // scrolled less than a half of buffered matrix
    // eslint-disable-next-line no-empty
    if (overlaps.max < HEATMAP_VIEW_PARAMS.overlapToFetch) {
      // eslint-disable-next-line no-useless-return
      return;
    }
    if (overlaps.max < 1) {
      this._retrieveData(heatmap, nextCachedData, sendRequest);
    } else if (overlaps.max > 1) {
      this._refreshData(heatmap, nextCachedData, sendRequest);
    }
  }

  parseHeatmapData(receivedData, storedHeatmap) {
    Logger.info('parseHeatmapData', receivedData, storedHeatmap);
    const { payload } = receivedData;

    if (get(payload, 'matrixName') !== get(storedHeatmap, 'matrixName')) {
      return;
    }

    if (payload.rowHeaders && payload.rowHeaders.items) {
      payload.rowHeaders.items.forEach((item) => {
        if (item.path) {
          // eslint-disable-next-line no-param-reassign
          item.path = this._getImagePath(item.path);
        }
      });
      this._extendHeaderData(storedHeatmap.headers.rows, payload.rowHeaders.items);
      // eslint-disable-next-line no-param-reassign
      storedHeatmap.rowsLoaded = true;
    }

    if (payload.colHeaders && payload.colHeaders.items) {
      payload.colHeaders.items.forEach((item) => {
        if (item.path) {
          // eslint-disable-next-line no-param-reassign
          item.path = this._getImagePath(item.path);
        }
      });
      this._extendHeaderData(storedHeatmap.headers.cols, payload.colHeaders.items);
      // eslint-disable-next-line no-param-reassign
      storedHeatmap.colsLoaded = true;
    }

    if (payload.data) {
      const startRow = payload.rowsStartIndex ? payload.rowsStartIndex - 1 : this._cachedData.firstRow;
      const startColumn = payload.colsStartIndex ? payload.colsStartIndex - 1 : this._cachedData.firstColumn;

      this._extendMatrixData(storedHeatmap.data, payload.data, startRow, startColumn);
      // eslint-disable-next-line no-param-reassign
      storedHeatmap.matrixLoaded = true;
    }

    this._cleanUp(storedHeatmap);
  }
}

export default new HeatmapDataService();
